import React, { useState } from 'react';
import {
  Plus,
  Edit2,
  Trash2,
  Send,
  Sparkles,
  Layers,
  CheckCircle2,
  AlertCircle,
} from 'lucide-react';
import { UserGroup, SavedSignatureTemplate, WorkspaceUser } from '../types';
import {
  saveUserGroup,
  deleteUserGroup,
} from '../services/templateGroupService';
import { injectSignaturesBatch } from '../services/api';
import { getStoredGoogleAccessToken, loginWithGoogle } from '../services/firebase';
import {
  assignUserGroupInFirestore,
  batchAssignUsersGroupInFirestore,
} from '../services/signaturePersistenceService';

interface GroupsManagerProps {
  domain: string;
  groups: UserGroup[];
  users: WorkspaceUser[];
  savedTemplates: SavedSignatureTemplate[];
  googleAccessToken: string | null;
  onGroupsUpdated: (groups: UserGroup[]) => void;
  onUsersUpdated: (users: WorkspaceUser[]) => void;
  onNavigateToEditorWithTemplate?: (templateId: string) => void;
}

export const GroupsManager: React.FC<GroupsManagerProps> = ({
  domain,
  groups,
  users,
  savedTemplates,
  googleAccessToken,
  onGroupsUpdated,
  onUsersUpdated,
  onNavigateToEditorWithTemplate,
}) => {
  const [selectedGroupId, setSelectedGroupId] = useState<string>(groups[0]?.id || 'group_main');
  const [isCreatingGroup, setIsCreatingGroup] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [newGroupDesc, setNewGroupDesc] = useState('');
  const [newGroupColor, setNewGroupColor] = useState('#0284c7');
  const [newGroupTemplateId, setNewGroupTemplateId] = useState(savedTemplates[0]?.id || 'tpl_default');

  // Group injection progress
  const [isInjectingGroup, setIsInjectingGroup] = useState(false);
  const [actionNotice, setActionNotice] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const activeGroup = groups.find((g) => g.id === selectedGroupId) || groups[0];
  const groupMembers = users.filter((u) => (u.groupId || 'group_main') === (activeGroup?.id || 'group_main'));
  const nonGroupMembers = users.filter((u) => (u.groupId || 'group_main') !== (activeGroup?.id || 'group_main'));

  const linkedTemplate = savedTemplates.find((t) => t.id === activeGroup?.savedTemplateId || t.templateId === activeGroup?.savedTemplateId) || savedTemplates[0];

  const handleCreateGroup = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newGroupName.trim()) return;

    const created = saveUserGroup(domain, {
      name: newGroupName.trim(),
      description: newGroupDesc.trim() || undefined,
      color: newGroupColor,
      savedTemplateId: newGroupTemplateId,
    });

    const updatedGroups = [...groups.filter((g) => g.id !== created.id), created];
    onGroupsUpdated(updatedGroups);
    setSelectedGroupId(created.id);
    setIsCreatingGroup(false);
    setNewGroupName('');
    setNewGroupDesc('');

    setActionNotice({
      type: 'success',
      text: `Utworzono nową grupę: ${created.name}!`,
    });
    setTimeout(() => setActionNotice(null), 4000);
  };

  const handleDeleteGroup = (groupId: string) => {
    if (groupId === 'group_main') {
      alert('Nie można usunąć Grupy Głównej.');
      return;
    }

    if (!confirm('Czy na pewno chcesz usunąć tę grupę? Jej członkowie zostaną przeniesieni do Grupy Głównej.')) {
      return;
    }

    try {
      deleteUserGroup(domain, groupId);
      const updatedGroups = groups.filter((g) => g.id !== groupId);
      onGroupsUpdated(updatedGroups);

      // Move members to main group
      const movedMembers = users.filter((u) => u.groupId === groupId);
      const updatedUsers = users.map((u) => (u.groupId === groupId ? { ...u, groupId: 'group_main' } : u));
      onUsersUpdated(updatedUsers);

      if (movedMembers.length > 0) {
        batchAssignUsersGroupInFirestore(domain, movedMembers, 'group_main').catch((err) =>
          console.warn('[Firestore Warning] Błąd aktualizacji grupy po usunięciu:', err)
        );
      }

      setSelectedGroupId('group_main');
      setActionNotice({
        type: 'success',
        text: 'Grupa została usunięta, a jej użytkownicy przypisani do Grupy Głównej.',
      });
      setTimeout(() => setActionNotice(null), 4000);
    } catch (e: any) {
      alert(e.message || 'Błąd usuwania grupy');
    }
  };

  const handleAssignMember = async (userId: string, targetGroupId: string) => {
    const targetGroup = groups.find((g) => g.id === targetGroupId);
    const targetUser = users.find((u) => u.id === userId || (u.primaryEmail && u.primaryEmail === userId));
    const targetTemplateId = targetGroup?.savedTemplateId;

    const updatedUsers = users.map((u) => {
      const isTarget = u.id === userId || (u.primaryEmail && u.primaryEmail === userId);
      if (isTarget) {
        return {
          ...u,
          groupId: targetGroupId,
          ...(targetTemplateId ? { assignedSignatureId: targetTemplateId, customTemplateId: targetTemplateId } : {}),
        };
      }
      return u;
    });
    onUsersUpdated(updatedUsers);

    try {
      await assignUserGroupInFirestore(
        domain,
        targetUser || { id: userId, primaryEmail: userId.includes('@') ? userId : '' },
        targetGroupId,
        targetTemplateId
      );
    } catch (e) {
      console.error('[Firestore Error] Błąd zapisu przypisania pracownika do grupy:', e);
    }
  };

  const handleChangeGroupTemplate = async (groupId: string, templateId: string) => {
    const target = groups.find((g) => g.id === groupId);
    if (!target) return;

    const updated = saveUserGroup(domain, {
      ...target,
      savedTemplateId: templateId,
    });

    const updatedGroups = groups.map((g) => (g.id === groupId ? updated : g));
    onGroupsUpdated(updatedGroups);

    // Propagate template to all members belonging to this group
    const groupMembersToUpdate = users.filter((u) => (u.groupId || 'group_main') === groupId);
    if (groupMembersToUpdate.length > 0) {
      const updatedUsers = users.map((u) =>
        (u.groupId || 'group_main') === groupId
          ? { ...u, assignedSignatureId: templateId, customTemplateId: templateId }
          : u
      );
      onUsersUpdated(updatedUsers);
      await batchAssignUsersGroupInFirestore(domain, groupMembersToUpdate, groupId, templateId);
    }

    setActionNotice({
      type: 'success',
      text: `Zaktualizowano szablon przypisany do grupy "${target.name}".`,
    });
    setTimeout(() => setActionNotice(null), 4000);
  };

  const handleInjectGroupSignatures = async () => {
    if (groupMembers.length === 0) {
      alert('Ta grupa nie ma żadnych przypisanych użytkowników.');
      return;
    }

    let token = googleAccessToken || getStoredGoogleAccessToken();
    if (!token) {
      try {
        const res = await loginWithGoogle();
        token = res.accessToken;
      } catch (authErr: any) {
        console.warn('Google sign-in skipped or cancelled:', authErr);
        alert('Do wstrzyknięcia stopek do Gmaila wymagana jest autoryzacja konta Google.');
        return;
      }
    }

    if (!token) {
      alert('Do wstrzyknięcia stopek do Gmaila wymagana jest autoryzacja konta Google.');
      return;
    }

    setIsInjectingGroup(true);
    setActionNotice(null);

    try {
      try {
        await injectSignaturesBatch(groupMembers, null, domain, token, savedTemplates);
      } catch (batchErr: any) {
        if (
          batchErr.message?.includes('BRAK_TOKENU_GOOGLE') ||
          batchErr.message?.includes('autoryzacji') ||
          batchErr.message?.includes('401')
        ) {
          const res = await loginWithGoogle();
          token = res.accessToken;
          if (token) {
            await injectSignaturesBatch(groupMembers, null, domain, token, savedTemplates);
          } else {
            throw batchErr;
          }
        } else {
          throw batchErr;
        }
      }

      // Mark group members as synced
      const updatedUsers = users.map((u) =>
        groupMembers.some((gm) => gm.id === u.id)
          ? { ...u, signatureStatus: 'synced' as const, lastSyncedAt: new Date().toISOString() }
          : u
      );
      onUsersUpdated(updatedUsers);

      setActionNotice({
        type: 'success',
        text: `Stopki dla ${groupMembers.length} użytkowników grupy "${activeGroup.name}" zostały pomyślnie zsynchronizowane w Gmailu!`,
      });
    } catch (err: any) {
      console.error('Group injection error:', err);
      setActionNotice({
        type: 'error',
        text: err.message || 'Wystąpił błąd podczas wstrzykiwania stopek grupy.',
      });
    } finally {
      setIsInjectingGroup(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24, width: '100%', maxWidth: 1300, margin: '0 auto' }}>
      {/* Top Banner */}
      <div
        style={{
          background: 'linear-gradient(135deg, rgba(2, 132, 199, 0.12) 0%, rgba(17, 24, 39, 0.95) 100%)',
          border: '1px solid rgba(2, 132, 199, 0.3)',
          borderRadius: 16,
          padding: '24px 28px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 16,
        }}
      >
        <div>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, color: '#0066FF', fontSize: 12, fontWeight: 600, marginBottom: 6 }}>
            <Layers size={16} />
            <span>Segmentacja podpisów i reguły działowe</span>
          </div>
          <h2 style={{ fontSize: 24, fontWeight: 800, color: '#0B192C', margin: 0 }}>
            Grupy Użytkowników & Dedykowane Szablony Stopek
          </h2>
          <p style={{ color: '#64748b', fontSize: 13, marginTop: 6, marginBottom: 0, maxWidth: 650 }}>
            Przypisz różne stopki do poszczególnych działów (np. dynamiczny baner CTA dla Sprzedaży, minimalistyczny szablon dla Zarządu).
          </p>
        </div>

        <button
          onClick={() => setIsCreatingGroup(true)}
          style={{
            background: '#0284c7',
            color: '#ffffff',
            border: 'none',
            borderRadius: 10,
            padding: '10px 18px',
            fontSize: 13,
            fontWeight: 600,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            boxShadow: '0 4px 14px rgba(2, 132, 199, 0.3)',
          }}
        >
          <Plus size={16} />
          <span>Utwórz nową grupę</span>
        </button>
      </div>

      {actionNotice && (
        <div
          style={{
            padding: '12px 18px',
            borderRadius: 10,
            fontSize: 13,
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            background: actionNotice.type === 'success' ? 'rgba(16, 185, 129, 0.15)' : 'rgba(239, 68, 68, 0.15)',
            border: `1px solid ${actionNotice.type === 'success' ? '#10b981' : '#ef4444'}`,
            color: actionNotice.type === 'success' ? '#6ee7b7' : '#fca5a5',
          }}
        >
          {actionNotice.type === 'success' ? <CheckCircle2 size={18} /> : <AlertCircle size={18} />}
          <span>{actionNotice.text}</span>
        </div>
      )}

      {/* Main Split View: Groups List on left, Selected Group Management on right */}
      <div className="groups-grid">
        {/* LEFT: Groups List */}
        <div className="card" style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', padding: '4px 8px 8px' }}>
            Twoje grupy ({groups.length})
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {groups.map((group) => {
              const count = users.filter((u) => (u.groupId || 'group_main') === group.id).length;
              const isSelected = group.id === selectedGroupId;

              return (
                <div
                  key={group.id}
                  onClick={() => setSelectedGroupId(group.id)}
                  style={{
                    padding: '12px 14px',
                    borderRadius: 10,
                    cursor: 'pointer',
                    background: isSelected ? '#eff6ff' : '#f8fafc',
                    border: isSelected ? '1px solid #0066FF' : '1px solid #e2e8f0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    transition: 'all 0.15s ease',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div
                      style={{
                        width: 12,
                        height: 12,
                        borderRadius: '50%',
                        background: group.color || '#0284c7',
                        flexShrink: 0,
                      }}
                    />
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 700, color: isSelected ? '#0066FF' : '#0B192C' }}>
                        {group.name}
                      </div>
                      <div style={{ fontSize: 11, color: '#64748b' }}>
                        {group.id === 'group_main' ? 'Domyślna grupa' : 'Grupa niestandardowa'}
                      </div>
                    </div>
                  </div>

                  <span
                    style={{
                      background: isSelected ? '#0066FF' : '#e2e8f0',
                      color: isSelected ? '#ffffff' : '#475569',
                      fontSize: 11,
                      fontWeight: 700,
                      padding: '2px 8px',
                      borderRadius: 999,
                    }}
                  >
                    {count}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        {/* RIGHT: Active Group Details & Member Management */}
        {activeGroup && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            {/* Group Header Card */}
            <div className="card" style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 18 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{ width: 16, height: 16, borderRadius: '50%', background: activeGroup.color }} />
                    <h3 style={{ fontSize: 20, fontWeight: 800, color: '#0B192C', margin: 0 }}>
                      {activeGroup.name}
                    </h3>
                  </div>
                  {activeGroup.description && (
                    <p style={{ fontSize: 13, color: '#64748b', margin: '6px 0 0' }}>
                      {activeGroup.description}
                    </p>
                  )}
                </div>

                <div style={{ display: 'flex', gap: 10 }}>
                  <button
                    onClick={handleInjectGroupSignatures}
                    disabled={isInjectingGroup || groupMembers.length === 0}
                    style={{
                      background: 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)',
                      color: '#ffffff',
                      border: 'none',
                      borderRadius: 10,
                      padding: '9px 16px',
                      fontSize: 13,
                      fontWeight: 600,
                      cursor: isInjectingGroup || groupMembers.length === 0 ? 'not-allowed' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      boxShadow: '0 2px 10px rgba(2, 132, 199, 0.3)',
                      opacity: isInjectingGroup || groupMembers.length === 0 ? 0.6 : 1,
                    }}
                  >
                    <Send size={15} />
                    <span>{isInjectingGroup ? 'Wstrzykiwanie...' : `Wstrzyknij stopki grupy (${groupMembers.length})`}</span>
                  </button>

                  {activeGroup.id !== 'group_main' && (
                    <button
                      onClick={() => handleDeleteGroup(activeGroup.id)}
                      title="Usuń grupę"
                      style={{
                        background: 'rgba(239, 68, 68, 0.1)',
                        border: '1px solid rgba(239, 68, 68, 0.3)',
                        color: '#ef4444',
                        borderRadius: 8,
                        padding: '9px 12px',
                        cursor: 'pointer',
                      }}
                    >
                      <Trash2 size={16} />
                    </button>
                  )}
                </div>
              </div>

              {/* Template Assignment Selector */}
              <div
                style={{
                  background: '#f8fafc',
                  border: '1px solid #e2e8f0',
                  borderRadius: 12,
                  padding: '16px 20px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  flexWrap: 'wrap',
                  gap: 16,
                }}
              >
                <div>
                  <div style={{ fontSize: 12, color: '#64748b', marginBottom: 4 }}>
                    Przypisany szablon podpisu dla tej grupy:
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Sparkles size={16} color="#0066FF" />
                    <strong style={{ color: '#0B192C', fontSize: 14 }}>
                      {linkedTemplate?.name || 'Szablon Domyślny'}
                    </strong>
                    <span style={{ fontSize: 11, color: '#64748b' }}>
                      ({linkedTemplate?.templateId})
                    </span>
                  </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <select
                    value={
                      savedTemplates.find((t) => t.id === activeGroup.savedTemplateId || t.templateId === activeGroup.savedTemplateId)?.id ||
                      activeGroup.savedTemplateId ||
                      'tpl_default'
                    }
                    onChange={(e) => handleChangeGroupTemplate(activeGroup.id, e.target.value)}
                    style={{
                      background: '#f1f5f9',
                      border: '1px solid #334155',
                      borderRadius: 8,
                      padding: '8px 12px',
                      color: '#0B192C',
                      fontSize: 13,
                      cursor: 'pointer',
                    }}
                  >
                    {savedTemplates.map((tpl) => (
                      <option key={tpl.id} value={tpl.id}>
                        {tpl.name}
                      </option>
                    ))}
                  </select>

                  {onNavigateToEditorWithTemplate && (
                    <button
                      onClick={() => onNavigateToEditorWithTemplate(linkedTemplate?.id || 'tpl_default')}
                      style={{
                        background: 'transparent',
                        border: '1px solid #334155',
                        borderRadius: 8,
                        padding: '8px 12px',
                        color: '#0066FF',
                        fontSize: 12,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6,
                      }}
                    >
                      <Edit2 size={13} />
                      <span>Edytuj szablon</span>
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Members Section */}
            <div className="card" style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                <h4 style={{ fontSize: 15, fontWeight: 700, color: '#0B192C', margin: 0 }}>
                  Członkowie grupy ({groupMembers.length})
                </h4>
                <span style={{ fontSize: 12, color: '#64748b' }}>
                  Wszyscy członkowie tej grupy używają szablonu: <strong style={{ color: '#0066FF' }}>{linkedTemplate?.name}</strong>
                </span>
              </div>

              {groupMembers.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '32px 16px', color: '#64748b', fontSize: 13 }}>
                  Ta grupa nie posiada jeszcze przypisanych pracowników. Możesz dodać użytkowników poniżej.
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {groupMembers.map((member) => (
                    <div
                      key={member.id}
                      style={{
                        padding: '10px 14px',
                        background: '#f8fafc',
                        border: '1px solid #e2e8f0',
                        borderRadius: 8,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        flexWrap: 'wrap',
                        gap: 10,
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        {member.avatarUrl ? (
                          <img
                            src={member.avatarUrl}
                            alt={member.fullName}
                            style={{ width: 32, height: 32, borderRadius: '50%', objectFit: 'cover', border: `1.5px solid ${activeGroup.color}` }}
                          />
                        ) : (
                          <div style={{ width: 32, height: 32, borderRadius: '50%', background: '#eff6ff', border: '1px solid #bfdbfe', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, color: '#0066FF' }}>
                            {member.fullName[0]}
                          </div>
                        )}
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 600, color: '#0B192C' }}>
                            {member.fullName}
                          </div>
                          <div style={{ fontSize: 11, color: '#64748b' }}>
                            {member.primaryEmail} &bull; {member.jobTitle || 'Pracownik'}
                          </div>
                        </div>
                      </div>

                      {activeGroup.id !== 'group_main' && (
                        <button
                          onClick={() => handleAssignMember(member.id, 'group_main')}
                          style={{
                            background: 'transparent',
                            border: '1px solid #334155',
                            color: '#64748b',
                            borderRadius: 6,
                            padding: '4px 10px',
                            fontSize: 11,
                            cursor: 'pointer',
                          }}
                        >
                          Przenieś do Głównej
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Add non-members to this group */}
              {nonGroupMembers.length > 0 && (
                <div style={{ borderTop: '1px solid #e2e8f0', paddingTop: 16 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: '#cbd5e1', marginBottom: 10 }}>
                    Dodaj pracowników do grupy &bdquo;{activeGroup.name}&rdquo;:
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {nonGroupMembers.map((nonMember) => (
                      <button
                        key={nonMember.id}
                        onClick={() => handleAssignMember(nonMember.id, activeGroup.id)}
                        style={{
                          background: '#f1f5f9',
                          border: '1px solid #334155',
                          borderRadius: 8,
                          padding: '6px 12px',
                          color: '#0B192C',
                          fontSize: 12,
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 6,
                        }}
                      >
                        <Plus size={13} color="#0284c7" />
                        <span>{nonMember.fullName}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Modal: Create New Group */}
      {isCreatingGroup && (
        <div className="modal-overlay">
          <div className="modal-card" style={{ padding: 24 }}>
            <h3 style={{ fontSize: 18, fontWeight: 700, color: '#0B192C', marginBottom: 16 }}>
              Utwórz nową grupę pracowników
            </h3>

            <form onSubmit={handleCreateGroup} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#cbd5e1', marginBottom: 6 }}>
                  Nazwa grupy (np. Dział Sprzedaży, Helpdesk, Zarząd)
                </label>
                <input
                  type="text"
                  required
                  placeholder="np. Dział Handlowy"
                  value={newGroupName}
                  onChange={(e) => setNewGroupName(e.target.value)}
                  style={{
                    width: '100%',
                    background: '#f8fafc',
                    border: '1px solid #e2e8f0',
                    borderRadius: 8,
                    padding: '10px 14px',
                    color: '#0B192C',
                    fontSize: 13,
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#cbd5e1', marginBottom: 6 }}>
                  Krótki opis
                </label>
                <input
                  type="text"
                  placeholder="np. Zespół odpowiedzialny za kontakt z klientem"
                  value={newGroupDesc}
                  onChange={(e) => setNewGroupDesc(e.target.value)}
                  style={{
                    width: '100%',
                    background: '#f8fafc',
                    border: '1px solid #e2e8f0',
                    borderRadius: 8,
                    padding: '10px 14px',
                    color: '#0B192C',
                    fontSize: 13,
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#cbd5e1', marginBottom: 6 }}>
                  Kolor wyróżniający grupę
                </label>
                <div style={{ display: 'flex', gap: 8 }}>
                  {['#0284c7', '#10b981', '#8b5cf6', '#f59e0b', '#ec4899', '#0d9488'].map((col) => (
                    <div
                      key={col}
                      onClick={() => setNewGroupColor(col)}
                      style={{
                        width: 28,
                        height: 28,
                        borderRadius: '50%',
                        background: col,
                        cursor: 'pointer',
                        border: newGroupColor === col ? '2px solid #ffffff' : '2px solid transparent',
                      }}
                    />
                  ))}
                </div>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#cbd5e1', marginBottom: 6 }}>
                  Przypisany szablon podpisu
                </label>
                <select
                  value={newGroupTemplateId}
                  onChange={(e) => setNewGroupTemplateId(e.target.value)}
                  style={{
                    width: '100%',
                    background: '#f8fafc',
                    border: '1px solid #e2e8f0',
                    borderRadius: 8,
                    padding: '10px 14px',
                    color: '#0B192C',
                    fontSize: 13,
                  }}
                >
                  {savedTemplates.map((tpl) => (
                    <option key={tpl.id} value={tpl.id}>
                      {tpl.name}
                    </option>
                  ))}
                </select>
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 12 }}>
                <button
                  type="button"
                  onClick={() => setIsCreatingGroup(false)}
                  style={{
                    background: 'transparent',
                    border: '1px solid #334155',
                    color: '#64748b',
                    borderRadius: 8,
                    padding: '8px 16px',
                    fontSize: 13,
                    cursor: 'pointer',
                  }}
                >
                  Anuluj
                </button>
                <button
                  type="submit"
                  style={{
                    background: '#0284c7',
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: 8,
                    padding: '8px 18px',
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  Zapisz grupę
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
